import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Button, Tooltip, IconButton, Chip,
  TextField, MenuItem, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ListIcon from '@mui/icons-material/List';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import type { GraphWorkspace } from '../../../shared/watchdogTypes.js';

/**
 * Watchdog · Graph-Ansicht (FR-2, ueberarbeitet nach FR-4).
 *
 * Bewusst ohne Graph-Bibliothek: Die Hierarchie ist ein Baum mit vier festen
 * Ebenen (Workspace → Domain → Event → Target) und v1 ist read-only. Ein
 * eigenes SVG-Layout ist dafuer kleiner als jede Lib und bringt keine neue
 * Abhaengigkeit in den Stack. Bearbeitet wird ueber den Expertenmodus (FR-1).
 *
 * FR-4 bringt drei Aenderungen:
 *   · zwei Ausrichtungen — nach rechts (wie bisher) und als Top-Down-Baum
 *   · progressive Entfaltung: Kanaele erscheinen erst auf Klick aufs Event
 *   · Kanaele farblich unterscheidbar statt einheitlicher Kaesten
 */

const NODE_W = 210;
const NODE_H = 46;
/** Abstand zwischen zwei Ebenen (die „Laenge" der Kanten). */
const LEVEL_GAP_LR = 90;
const LEVEL_GAP_TB = 62;
/** Abstand zwischen zwei Geschwistern derselben Ebene. */
const SLOT_GAP_LR = 12;
const SLOT_GAP_TB = 26;

type Orientation = 'lr' | 'tb';

/** Farbe nach effektivem Status — die logisch vererbte Pause faerbt mit. */
const STATUS_COLOR: Record<string, string> = {
  active: colors.scoreGreen,
  paused: colors.scoreYellow,
  disabled: colors.black60,
  vanished: colors.scoreRed,
  new: colors.black60,
  discovery_running: colors.scoreYellow,
  failed: colors.scoreRed,
};

/**
 * Kanal-Erkennungsfarben (FR-4): markennah, damit man den Kanal am Node sofort
 * sieht statt vier gleiche Kaesten zu vergleichen. Google Ads, Meta und
 * LinkedIn sind alle blau — das traegt die Farbe allein nicht, deshalb steht
 * zusaetzlich das Kuerzel im Node.
 */
const CHANNEL_STYLE: Record<string, { color: string; short: string }> = {
  google_ads: { color: '#4285F4', short: 'Ads' },
  ga4: { color: '#E8710A', short: 'GA4' },
  meta: { color: '#0866FF', short: 'Meta' },
  linkedin: { color: '#0A66C2', short: 'in' },
  microsoft_uet: { color: '#00A4EF', short: 'UET' },
  tiktok: { color: '#EE1D52', short: 'TT' },
};

/** Unbekannte Kanaele bekommen eine stabile Farbe statt Grau-in-Grau. */
function channelStyle(channel: string): { color: string; short: string } {
  const known = CHANNEL_STYLE[channel];
  if (known) return known;
  let hash = 0;
  for (let i = 0; i < channel.length; i++) hash = (hash * 31 + channel.charCodeAt(i)) % 360;
  return { color: `hsl(${hash} 55% 45%)`, short: channel.slice(0, 3).toUpperCase() };
}

interface LayoutNode {
  id: string;
  kind: 'workspace' | 'domain' | 'event' | 'target';
  title: string;
  subtitle: string;
  status: string;
  /** Status weicht vom eigenen ab, weil er von oben vererbt wurde. */
  inherited: boolean;
  focus: boolean;
  x: number;
  y: number;
  domainId?: string;
  /** Nur bei Events: eigene Kennung, Zahl der Kanaele und Klappzustand. */
  eventId?: string;
  childCount?: number;
  expanded?: boolean;
  /** Nur bei Targets: Kanal-Schluessel fuer Farbe und Kuerzel. */
  channel?: string;
}

interface LayoutEdge { from: string; to: string; dimmed: boolean }

/**
 * Baut das Layout in abstrakten Slots: `level` ist die Ebene (0–3), `slot` die
 * Position innerhalb der Ebene. Erst am Ende wird daraus x/y — deshalb kostet
 * die zweite Ausrichtung keine zweite Layout-Logik.
 */
function buildLayout(data: GraphWorkspace[], expanded: Set<string>, orientation: Orientation) {
  const raw: (Omit<LayoutNode, 'x' | 'y'> & { level: number; slot: number })[] = [];
  const edges: LayoutEdge[] = [];
  let slot = 0;

  for (const ws of data) {
    const wsId = `ws:${ws.workspace_ref ?? '_none'}`;
    const wsStart = slot;

    for (const dom of ws.domains) {
      const domStart = slot;

      for (const ev of dom.events) {
        const evStart = slot;
        const showTargets = expanded.has(ev.id) && ev.targets.length > 0;

        if (showTargets) {
          for (const t of ev.targets) {
            raw.push({
              id: `t:${t.id}`, kind: 'target',
              title: t.channel_display || t.channel,
              subtitle: t.conversion_label || t.channel_id || '—',
              status: t.effective_status,
              inherited: t.effective_status !== t.status,
              focus: false,
              level: 3, slot,
              domainId: dom.id,
              eventId: ev.id,
              channel: t.channel,
            });
            edges.push({ from: `e:${ev.id}`, to: `t:${t.id}`, dimmed: t.effective_status !== 'active' });
            slot += 1;
          }
        } else {
          // Eingeklappt (oder ohne Kanaele): das Event belegt selbst einen Slot.
          slot += 1;
        }

        raw.push({
          id: `e:${ev.id}`, kind: 'event',
          title: ev.event_name,
          subtitle: ev.event_type === 'unclear' ? 'nicht zugeordnet' : ev.event_type,
          status: ev.effective_status,
          inherited: ev.effective_status !== ev.status,
          focus: ev.focus_event,
          level: 2,
          slot: showTargets ? (evStart + slot - 1) / 2 : evStart,
          domainId: dom.id,
          eventId: ev.id,
          childCount: ev.targets.length,
          expanded: showTargets,
        });
        edges.push({ from: `d:${dom.id}`, to: `e:${ev.id}`, dimmed: ev.effective_status !== 'active' });
      }

      if (dom.events.length === 0) slot += 1;

      raw.push({
        id: `d:${dom.id}`, kind: 'domain',
        title: dom.display_name || dom.hostname,
        subtitle: dom.gtm_container_id ?? dom.hostname,
        status: dom.domain_status,
        inherited: false,
        focus: false,
        level: 1,
        slot: dom.events.length ? (domStart + slot - 1) / 2 : domStart,
        domainId: dom.id,
        childCount: dom.events.length,
      });
      edges.push({ from: wsId, to: `d:${dom.id}`, dimmed: dom.domain_status !== 'active' });
      slot += 0.5; // Luft zwischen zwei Domains
    }

    if (ws.domains.length === 0) slot += 1;
    raw.push({
      id: wsId, kind: 'workspace',
      title: ws.workspace_name ?? ws.workspace_ref ?? 'ohne Referenz',
      subtitle: `${ws.domains.length} Domain(s)`,
      status: 'active',
      inherited: false,
      focus: false,
      level: 0,
      slot: ws.domains.length ? (wsStart + slot - 0.5) / 2 : wsStart,
    });
    slot += 1; // Luft zwischen zwei Workspaces
  }

  const levelGap = orientation === 'lr' ? NODE_W + LEVEL_GAP_LR : NODE_H + LEVEL_GAP_TB;
  const slotGap = orientation === 'lr' ? NODE_H + SLOT_GAP_LR : NODE_W + SLOT_GAP_TB;

  const nodes: LayoutNode[] = raw.map(({ level, slot: s, ...rest }) => ({
    ...rest,
    x: orientation === 'lr' ? level * levelGap : s * slotGap,
    y: orientation === 'lr' ? s * slotGap : level * levelGap,
  }));

  const maxSlot = raw.reduce((m, n) => Math.max(m, n.slot), 0);
  const width = orientation === 'lr' ? 3 * levelGap + NODE_W : maxSlot * slotGap + NODE_W;
  const height = orientation === 'lr' ? maxSlot * slotGap + NODE_H : 3 * levelGap + NODE_H;
  return { nodes, edges, width, height };
}

export default function WatchdogGraphPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<GraphWorkspace[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 20, y: 20 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  /** Domain-Filter: '' = alle Domains, sonst die Domain-ID. */
  const [domainFilter, setDomainFilter] = useState('');
  const [orientation, setOrientation] = useState<Orientation>('lr');
  /** Events, deren Kanaele ausgeklappt sind (FR-4: progressive Entfaltung). */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<GraphWorkspace[]>('/watchdog/graph');
    setLoading(false);
    if (!res.success) { setError(res.error.message); return; }
    setError(null);
    setData(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Alle Domains flach, für das Auswahl-Dropdown. */
  const allDomains = useMemo(
    () => (data ?? []).flatMap((w) => w.domains.map((d) => ({ id: d.id, label: d.display_name || d.hostname, ws: w.workspace_ref }))),
    [data],
  );

  /** Auf eine Domain eingeschränkte Sicht — leere Workspaces fallen weg. */
  const visibleData = useMemo(() => {
    if (!data) return null;
    if (!domainFilter) return data;
    return data
      .map((w) => ({ ...w, domains: w.domains.filter((d) => d.id === domainFilter) }))
      .filter((w) => w.domains.length > 0);
  }, [data, domainFilter]);

  const layout = useMemo(
    () => (visibleData ? buildLayout(visibleData, expanded, orientation) : null),
    [visibleData, expanded, orientation],
  );
  const wrapRef = useRef<HTMLDivElement | null>(null);

  /** Alle Events mit Kanaelen — Grundlage für „alles auf/zu". */
  const eventsWithTargets = useMemo(
    () => (visibleData ?? []).flatMap((w) => w.domains).flatMap((d) => d.events).filter((e) => e.targets.length > 0),
    [visibleData],
  );
  const allExpanded = eventsWithTargets.length > 0 && eventsWithTargets.every((e) => expanded.has(e.id));

  /**
   * Beim Laden die Achse einpassen, die zur Ausrichtung gehoert: nach rechts die
   * Breite, im Baum die Hoehe. Beides gleichzeitig einzupassen wuerde bei vielen
   * Knoten so weit herauszoomen, dass die Beschriftungen unlesbar werden — die
   * andere Achse wird stattdessen gescrollt bzw. geschoben.
   */
  const fitToView = useCallback(() => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box || !layout) return;
    const scale = orientation === 'lr'
      ? (box.width - 40) / layout.width
      : Math.min((box.width - 40) / layout.width, (box.height - 40) / layout.height);
    setZoom(Math.max(0.3, Math.min(1, scale)));
    setPan({ x: 20, y: 20 });
  }, [layout, orientation]);

  useEffect(() => { fitToView(); }, [fitToView]);

  const nodeById = useMemo(() => {
    const m = new Map<string, LayoutNode>();
    layout?.nodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [layout]);

  function toggleEvent(eventId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }

  /**
   * FR-4: Der Klick trifft das, was angeklickt wurde — vorher sprang jeder
   * Knoten auf die Domainansicht, auch Events und Kanaele.
   *   Event mit Kanaelen → auf/zuklappen
   *   Domain, Kanal, Event ohne Kanaele → in der Liste oeffnen
   *   Workspace → nichts
   */
  function onNodeClick(n: LayoutNode) {
    if (n.kind === 'event' && (n.childCount ?? 0) > 0) {
      toggleEvent(n.eventId!);
      return;
    }
    if (!n.domainId) return;
    const params = new URLSearchParams({ open: n.domainId });
    if (n.eventId) params.set('event', n.eventId);
    navigate(`/watchdog/domains?${params.toString()}`);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>Graph</Typography>
          <Typography variant="body2" sx={{ color: colors.black60 }}>
            Workspace-Referenz → Domain → Events → Targets. Klick auf ein Event zeigt seine Kanäle,
            Klick auf Domain oder Kanal öffnet sie in der Liste.
            Die Ansicht ist read-only — geändert wird im Expertenmodus.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField
            select size="small" value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            sx={{ minWidth: 230, mr: 1 }}
            InputProps={{ sx: { fontSize: 13.5 } }}
            // Ohne displayEmpty zeigt MUI bei value="" gar nichts an —
            // „Alle Domains" waere dann ein leeres Feld.
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" sx={{ fontSize: 13.5 }}>
              Alle Domains{allDomains.length ? ` (${allDomains.length})` : ''}
            </MenuItem>
            {allDomains.map((d) => (
              // Das Leerzeichen gehoert in den Text, nicht in die Formatierung:
              // im geschlossenen Feld rendert MUI den Inhalt flach.
              <MenuItem key={d.id} value={d.id} sx={{ fontSize: 13.5 }}>
                {d.label}
                {d.ws && <Typography component="span" variant="caption" sx={{ color: colors.black60 }}>{' · ' + d.ws}</Typography>}
              </MenuItem>
            ))}
          </TextField>

          <ToggleButtonGroup
            size="small" exclusive value={orientation}
            onChange={(_, v: Orientation | null) => v && setOrientation(v)}
            sx={{ mr: 0.5 }}
          >
            <ToggleButton value="lr" sx={{ px: 1 }}>
              <Tooltip title="Nach rechts"><ArrowForwardIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="tb" sx={{ px: 1 }}>
              <Tooltip title="Top-Down-Baum"><ArrowDownwardIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={allExpanded ? 'Alle Kanäle einklappen' : 'Alle Kanäle ausklappen'}>
            <span>
              <IconButton
                size="small"
                disabled={eventsWithTargets.length === 0}
                onClick={() => setExpanded(allExpanded ? new Set() : new Set(eventsWithTargets.map((e) => e.id)))}
              >
                {allExpanded ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Verkleinern"><IconButton size="small" onClick={() => setZoom((z) => Math.max(0.3, z - 0.15))}><ZoomOutIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Vergrößern"><IconButton size="small" onClick={() => setZoom((z) => Math.min(2, z + 0.15))}><ZoomInIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Ansicht einpassen"><IconButton size="small" onClick={fitToView}><CenterFocusStrongIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Neu laden"><IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
          <Button size="small" variant="outlined" startIcon={<ListIcon />} onClick={() => navigate('/watchdog/domains')}
            sx={{ borderColor: colors.silver, color: colors.black85, ml: 0.5 }}>
            Liste
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Legend />

      <Paper ref={wrapRef} elevation={0} sx={{ border: `1px solid ${colors.silver}`, overflow: 'hidden', position: 'relative', height: '68vh' }}>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : !layout || layout.nodes.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center', color: colors.black60 }}>
            <Typography variant="body2">
              {domainFilter
                ? 'Für diese Auswahl gibt es nichts anzuzeigen.'
                : 'Noch keine Domain angelegt — der Graph ist leer.'}
            </Typography>
          </Box>
        ) : (
          <svg
            width="100%" height="100%"
            style={{ cursor: drag.current ? 'grabbing' : 'grab', display: 'block' }}
            onMouseDown={(e) => { drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y }; }}
            onMouseMove={(e) => {
              if (!drag.current) return;
              setPan({ x: drag.current.px + (e.clientX - drag.current.x), y: drag.current.py + (e.clientY - drag.current.y) });
            }}
            onMouseUp={() => { drag.current = null; }}
            onMouseLeave={() => { drag.current = null; }}
          >
            <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
              {/* Kanten zuerst, damit die Knoten darueber liegen */}
              {layout.edges.map((e, i) => {
                const a = nodeById.get(e.from);
                const b = nodeById.get(e.to);
                if (!a || !b) return null;
                // Nach rechts: Kante verlaesst die rechte Kante und trifft links.
                // Im Baum: unten raus, oben rein.
                const [x1, y1, x2, y2] = orientation === 'lr'
                  ? [a.x + NODE_W, a.y + NODE_H / 2, b.x, b.y + NODE_H / 2]
                  : [a.x + NODE_W / 2, a.y + NODE_H, b.x + NODE_W / 2, b.y];
                const d = orientation === 'lr'
                  ? `M ${x1} ${y1} C ${(x1 + x2) / 2} ${y1}, ${(x1 + x2) / 2} ${y2}, ${x2} ${y2}`
                  : `M ${x1} ${y1} C ${x1} ${(y1 + y2) / 2}, ${x2} ${(y1 + y2) / 2}, ${x2} ${y2}`;
                return (
                  <path
                    key={i}
                    d={d}
                    fill="none"
                    stroke={e.dimmed ? colors.silver : '#9aa7b4'}
                    strokeWidth={e.dimmed ? 1 : 1.5}
                    strokeDasharray={e.dimmed ? '4 3' : undefined}
                  />
                );
              })}

              {layout.nodes.map((n) => <GraphNode key={n.id} node={n} onClick={() => onNodeClick(n)} />)}
            </g>
          </svg>
        )}
      </Paper>
    </Box>
  );
}

function GraphNode({ node, onClick }: { node: LayoutNode; onClick: () => void }) {
  const statusColor = STATUS_COLOR[node.status] ?? colors.black60;
  const dim = node.status !== 'active';
  const isWs = node.kind === 'workspace';
  const ch = node.kind === 'target' && node.channel ? channelStyle(node.channel) : null;
  // Der Balken links traegt beim Kanal die Kanalfarbe, sonst den Status. Der
  // Status bleibt trotzdem sichtbar — ueber den Rahmen.
  const barColor = ch ? ch.color : statusColor;
  const collapsible = node.kind === 'event' && (node.childCount ?? 0) > 0;
  const clickable = collapsible || Boolean(node.domainId);

  return (
    <g transform={`translate(${node.x} ${node.y})`} onClick={onClick} style={{ cursor: clickable ? 'pointer' : 'default' }}>
      <rect
        width={NODE_W} height={NODE_H} rx={6}
        fill={isWs ? colors.lightGrey : '#ffffff'}
        stroke={node.focus ? colors.lime : statusColor}
        strokeWidth={node.focus ? 2.5 : 1.4}
        opacity={dim ? 0.65 : 1}
      />
      {/* Balken links: Kanalfarbe beim Target, sonst Status */}
      <rect width={4} height={NODE_H} rx={2} fill={barColor} opacity={dim ? 0.7 : 1} />

      <text x={14} y={19} style={{ fontSize: 12.5, fontWeight: node.focus ? 700 : 600, fill: colors.black85 }}>
        {node.focus ? '★ ' : ''}{truncate(node.title, ch ? 18 : 24)}
      </text>
      <text x={14} y={34} style={{ fontSize: 10.5, fill: colors.black60, fontFamily: "'JetBrains Mono', monospace" }}>
        {truncate(node.subtitle, ch ? 22 : 28)}
      </text>

      {/* Kanal-Kuerzel: die Farbe allein reicht nicht, drei Kanaele sind blau */}
      {ch && (
        <g transform={`translate(${NODE_W - 46} 9)`}>
          <rect width={38} height={16} rx={8} fill={ch.color} opacity={dim ? 0.55 : 0.92} />
          <text x={19} y={11.5} textAnchor="middle" style={{ fontSize: 9.5, fontWeight: 700, fill: '#ffffff' }}>
            {ch.short}
          </text>
        </g>
      )}

      {/* Zahl der Kanaele am Event, plus Klapp-Zeichen */}
      {collapsible && (
        <g transform={`translate(${NODE_W - 42} 9)`}>
          <rect width={34} height={16} rx={8} fill={colors.lightGrey} stroke={colors.silver} strokeWidth={0.8} />
          <text x={17} y={11.5} textAnchor="middle" style={{ fontSize: 9.5, fontWeight: 700, fill: colors.black85 }}>
            {node.expanded ? '−' : '+'} {node.childCount}
          </text>
          <title>
            {node.expanded
              ? `${node.childCount} Kanal/Kanäle — klicken zum Einklappen`
              : `${node.childCount} Kanal/Kanäle — klicken zum Anzeigen`}
          </title>
        </g>
      )}

      {node.inherited && (
        <>
          <title>{`Status „${node.status}" ist von oben vererbt — am Datensatz selbst steht etwas anderes.`}</title>
          <text x={14} y={NODE_H - 3} style={{ fontSize: 11, fontWeight: 700, fill: statusColor }}>↓</text>
        </>
      )}
    </g>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function Legend() {
  const status = [
    { label: 'aktiv', color: colors.scoreGreen },
    { label: 'pausiert', color: colors.scoreYellow },
    { label: 'deaktiviert', color: colors.black60 },
    { label: 'verschwunden', color: colors.scoreRed },
  ];
  const channels = Object.entries(CHANNEL_STYLE);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        {status.map((i) => (
          <Box key={i.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: i.color }} />
            <Typography variant="caption" sx={{ color: colors.black60 }}>{i.label}</Typography>
          </Box>
        ))}
        <Chip size="small" variant="outlined" label="★ Fokus-Event"
          sx={{ height: 20, fontSize: 11, borderColor: colors.lime, color: colors.black85 }} />
        <Typography variant="caption" sx={{ color: colors.black60, whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ mr: 0.5, fontWeight: 700 }}>↓</Box>
          Status von oben vererbt (Pause wird nie geschrieben)
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, mt: 0.75 }}>
        <Typography variant="caption" sx={{ color: colors.black60 }}>Kanäle:</Typography>
        {channels.map(([key, s]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
            <Typography variant="caption" sx={{ color: colors.black60 }}>{s.short}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
